import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import {
  Button,
  Icon,
  LoadMoreButton,
  NoDataAvailable,
  Table,
  Toggle,
} from 'jpm-adr-pattern-library';

import Pagination from './Pagination';

import Filters from './Filters';

import '../../../styles/components/TableViewPage.scss';

const TableViewPage = props => {
  const {
    classModifier,
    data,
    fields,
    pagination,
    heading,
    headerColumns,
    customExportButton,
    isLoading,
    onExport,
    onFilterChange,
    onLoadMore,
    onFetchData,
    canReset,
    onResetFilters,
    searchInputProps,
    displayNoDataAvailable,
    isError,
    isMultiPage,
    values,
  } = props;

  const {
    hasMore,
    limit,
    offset,
    totalItems
  } = pagination;

  const counter = offset + limit > totalItems
    ? totalItems
    : offset + limit;

  const [pageIndex, setPageIndex] = useState(0);
  const [canNextPage, setCanNextPage] = useState(false);
  const [canPreviousPage, setCanPreviousPage] = useState(false);
  const [pagesCount, setPagesCount] = useState(0);

  const scrollToTop = () => {
    setTimeout(() => {
      window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
      });
    })
  }

  useEffect(() => {
    if (data) {
      setPagesCount(Math.ceil(totalItems / limit));
    }
  },[data, totalItems]);

  useEffect(() => {
    const canNext = pageIndex < Math.trunc(totalItems / limit);
    setCanNextPage(canNext);
    const canPrevious = pageIndex > 0;
    setCanPreviousPage(canPrevious);
  }, [pageIndex, totalItems, limit]);

  const fetchData = useCallback(() => {
    onFetchData({ currentPosition: getPageIndex(), limit });
  }, [onFetchData, pageIndex, getPageIndex, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const gotoNextPage = () => {
    const newIndex = pageIndex + 1;
    setPageIndex(newIndex);
    scrollToTop();
  }

  const gotoPreviousPage = () => {
    const newIndex = pageIndex - 1;
    setPageIndex(newIndex);
    scrollToTop();
  }

  const gotoPage = index => {
    setPageIndex(index);
    scrollToTop();
  }

  const getPageIndex = () => pageIndex * limit;

  const button = (
    <Button
      customClass="export-table-tablet btn"
      title="Export Table"
      icon="Download"
      border="0"
      onClick={onExport}
    >
      Export Table
    </Button>
  );

  const hasFilters = fields.length && fields.length > 1;

  return (
    <section className={`columns table-view ${classModifier}`}>
      {hasFilters && (
        <Toggle
          defaultClosed={false}
          resetToDefault={onResetFilters}
          canReset={canReset}
        >
          <Filters
            fields={fields}
            onChange={onFilterChange}
            onClear={onResetFilters}
            values={values}
          />
        </Toggle>
      )}

      <div className="column is-12-desktop is-12-tablet main-wrapper">
        <div className="main-section-header">
          <div className="title-left-row">
            <h1 className="table-title">{heading}</h1>
          </div>

          <div className="tablet-button-wrapper right-container" aria-hidden>
            <div className="SearchComponent" aria-hidden>
              <input className="SearchComponent__input" {...searchInputProps} />

              <span className="SearchComponent__icon">
                <Icon iconName="SearchIcon" />
              </span>
            </div>

            <div className="is-hidden-desktop is-hidden-mobile">
              {customExportButton || button}
            </div>
          </div>
        </div>
        <div>
          {totalItems > 0 && (
            <div className="pagination-indicator">{`Displaying ${isMultiPage ? getPageIndex() + 1 : 1} - ${counter} of ${totalItems}`}</div>
          )}
          <Table
            isLoading={isLoading}
            values={data}
            tableId="TableView"
            tableCustomCss="table-view-table table is-striped is-narrow is-hoverable is-fullwidth"
            theadCustomCss=""
            tableTitles={headerColumns}
            statusTitleClosed="Closed"
            statusTitleOpen="Open"
          />
          {hasMore && !isMultiPage && (
            <div>
              <LoadMoreButton fetchFunction={onLoadMore} />
            </div>
          )}
          {isMultiPage && (
            <Pagination
              gotoNextPage={gotoNextPage}
              gotoPreviousPage={gotoPreviousPage}
              canNextPage={canNextPage}
              canPreviousPage={canPreviousPage}
              pagesCount={pagesCount}
              pageIndex={pageIndex}
              gotoPage={gotoPage}
            />
          )}
          {displayNoDataAvailable &&
            !isError && (
              <div>
                <NoDataAvailable
                  message="No Results Found"
                  wrapperClass="no-results-wrapper"
                  action={(
                    <Button
                      aria-label="Pressing ENTER or SPACEBAR key, filters will reset to default and the results will be reloaded."
                      onClick={() => {
                        onResetFilters();
                      }}
                      title="Reset filters"
                    >
                      Reset Filters To Default
                    </Button>
                  )}
                />
              </div>
            )}
          {isError && (
            <NoDataAvailable
              messageTitle="Data not found"
              customMessageTitleClass="Data__Not__Found-Title"
              customWrapperCSS="No__Data__Available-Custom-Wrapper"
              message="Either something went wrong or the data doesn’t exist."
              messageStyles="Data__Not__Found-Message"
              wrapperClass="Data__Not__Found"
            />
          )}
        </div>
      </div>
    </section>
  );
};

TableViewPage.defaultProps = {
  classModifier: '',
  customExportButton: null,
  displayNoDataAvailable: false,
  headerColumns: [],
  isError: false,
  isMultiPage: false,
  onSelectExport: null,
  canReset: false,
};

TableViewPage.propTypes = {
  classModifier: PropTypes.string,
  customExportButton: PropTypes.node,
  canReset: PropTypes.bool,
  data: PropTypes.array.isRequired,
  displayNoDataAvailable: PropTypes.bool,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select']),
    })
  ).isRequired,
  headerColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      customCSSClass: PropTypes.string,
      sortable: PropTypes.bool,
      handleSort: PropTypes.func,
      sortName: PropTypes.string,
      sortDirection: PropTypes.string,
    })
  ),
  heading: PropTypes.string.isRequired,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  isMultiPage: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  onFetchData: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onSelectExport: PropTypes.func,
  onSort: PropTypes.func.isRequired,
  pagination: PropTypes.shape({}).isRequired,
  searchInputProps:PropTypes.shape({
  }).isRequired,
  values: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
    'aria-label': PropTypes.string,
  }).isRequired
};

export default TableViewPage;
